import {
  DeleteOutlined,
  DownloadOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  InboxOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  classifyDHash,
  DHashSimilarity,
  hammingDistance,
} from '@mahawi/eshop-common/modules/image';
import {
  type IPhoto,
  type IPhotoDimension,
} from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import {
  Button,
  Col,
  Divider,
  Form,
  type FormListFieldData,
  type FormListOperation,
  Image,
  Input,
  Popconfirm,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import React, { type CSSProperties, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { ENotificationSeverity } from '../../../admin-types';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type NotifyType, useNotify } from '../../notification';
import {
  photoDelete,
  photoDownloadFromUrlsAction,
  photoPositionSave,
  photoVisibleChange,
} from '../../reducers/product/reducer';
import { COLOR, FONT_SIZE, SPACING } from '../../theme/tokens';

const { Title } = Typography;

interface IFormUrl {
  url: string;
}

interface IForm {
  urls: IFormUrl[];
}

const { Dragger } = Upload;

const GROUP_COLORS: string[] = [
  COLOR.red[4]!,
  COLOR.orange.primary!,
  COLOR.gold.primary!,
  COLOR.green.primary!,
  COLOR.blue.primary!,
  COLOR.purple.primary!,
  COLOR.magenta.primary!,
  COLOR.cyan.primary!,
];

function SortablePhotoCard({
  photo,
  productUUID,
  similarity,
  groupColor,
  onDelete,
  onVisibleChange,
}: {
  photo: IPhoto;
  productUUID: string;
  similarity?: DHashSimilarity;
  groupColor?: string;
  onDelete: (productUUID: string, photoUUID: string) => void;
  onVisibleChange: (
    productUUID: string,
    photoUUID: string,
    isVisible: boolean,
  ) => void;
}): React.ReactElement {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.uuid });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  let dimensions: IPhotoDimension | undefined;
  if (Array.isArray(photo.dimension)) {
    dimensions = photo.dimension.find(
      (d: IPhotoDimension): boolean => d.type === 'original',
    );
  }

  const src = `/static/product/photo/${photo.uuid}.webp`;

  let positionTag: React.ReactElement;
  if (photo.position === 1) {
    positionTag = <Tag color="green">Primary</Tag>;
  } else if (photo.position === 2) {
    positionTag = <Tag color="blue">Secondary</Tag>;
  } else {
    positionTag = <Tag>#{photo.position}</Tag>;
  }

  let similarityTag: React.ReactElement | null = null;
  if (similarity === DHashSimilarity.IDENTICAL) {
    similarityTag = <Tag color="red">Identical</Tag>;
  } else if (similarity === DHashSimilarity.DUPLICATE) {
    similarityTag = <Tag color="orange">Duplicate</Tag>;
  } else if (similarity === DHashSimilarity.SIMILAR) {
    similarityTag = <Tag color="gold">Similar</Tag>;
  }

  const visibilityTag = !photo.isVisible ? (
    <Tag color="default" icon={<EyeInvisibleOutlined />}>
      Hidden
    </Tag>
  ) : null;

  const cardStyle: CSSProperties = {
    ...style,
    outline: `3px solid ${groupColor ?? 'transparent'}`,
    outlineOffset: 2,
    borderRadius: 8,
  };

  return (
    <Col
      span={4}
      ref={setNodeRef}
      style={cardStyle}
      {...attributes}
      {...listeners}
    >
      <Space orientation="vertical" style={{ width: '100%' }}>
        <Space size={SPACING.xs} wrap>
          {positionTag}
          {similarityTag}
          {visibilityTag}
        </Space>

        <div style={{ position: 'relative', width: '100%' }}>
          <div
            style={{
              width: '100%',
              aspectRatio: '4/3',
              overflow: 'hidden',
              borderRadius: 6,
              opacity: photo.isVisible ? 1 : 0.4,
            }}
          >
            <Image
              src={src}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
              styles={{
                root: { display: 'block', width: '100%', height: '100%' },
              }}
              fallback="data:image/png;base64,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"
            />
          </div>
          <Space
            style={{
              position: 'absolute',
              top: SPACING.sm,
              right: SPACING.sm,
              zIndex: 10,
            }}
          >
            <Button
              type="default"
              icon={
                photo.isVisible ? (
                  <EyeOutlined style={{ fontSize: FONT_SIZE.xl }} />
                ) : (
                  <EyeInvisibleOutlined style={{ fontSize: FONT_SIZE.xl }} />
                )
              }
              style={{ opacity: 0.85 }}
              onClick={(): void =>
                onVisibleChange(productUUID, photo.uuid, !photo.isVisible)
              }
            />
            <Popconfirm
              title="Delete this photo?"
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={(): void => onDelete(productUUID, photo.uuid)}
            >
              <Tooltip title="Delete photo">
                <Button
                  danger
                  type="primary"
                  icon={<DeleteOutlined style={{ fontSize: FONT_SIZE.xl }} />}
                  style={{ opacity: 0.85 }}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        </div>

        <Space size={SPACING.xs}>
          <small>
            {dimensions?.width} x {dimensions?.height}
          </small>
          <a
            download={`${productUUID}--${photo.uuid}.webp`}
            href={`/static/product/photo/${photo.uuid}.webp`}
            target="_blank"
            rel="noreferrer noopener nofollow"
            aria-label="Download image"
          >
            <DownloadOutlined />
          </a>
        </Space>
      </Space>
    </Col>
  );
}

function Photo(): React.ReactElement {
  const notify: NotifyType = useNotify();
  const dispatch = useAppDispatch();
  const productPhotos = useSelector((s: RootState) => s.Product.photos);
  const productUUID = useSelector((s: RootState) => s.Product.uuid);
  const productInProcess = useSelector((s: RootState) => s.Product.inProcess);

  const [form] = Form.useForm<IForm>();

  const [localPhotos, setLocalPhotos] = useState<IPhoto[]>(
    [...productPhotos]
      .sort((a, b) => a.position - b.position)
      .map((p, i) => ({ ...p, position: i + 1 })),
  );

  const localPhotosRef = useRef<IPhoto[]>(localPhotos);
  localPhotosRef.current = localPhotos;

  const serverPhotosKey = productPhotos
    .map(
      (p) => `${p.uuid}:${String(p.isVisible)}:${String(p.dimension !== null)}`,
    )
    .sort()
    .join(',');

  useEffect((): void => {
    const serverMap = new Map(productPhotos.map((p) => [p.uuid, p]));
    const current = localPhotosRef.current;

    const preserved = current
      .filter((p) => serverMap.has(p.uuid))
      .map((p) => ({ ...serverMap.get(p.uuid)!, position: p.position }));

    const localUUIDs = new Set(current.map((p) => p.uuid));
    const added = productPhotos
      .filter((p) => !localUUIDs.has(p.uuid))
      .sort((a, b) => a.position - b.position);

    setLocalPhotos(
      [...preserved, ...added].map((p, i) => ({ ...p, position: i + 1 })),
    );
  }, [serverPhotosKey]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd({ active, over }: DragEndEvent): void {
    if (!over || active.id === over.id) return;
    setLocalPhotos((prev: IPhoto[]) => {
      const oldIndex = prev.findIndex((p) => p.uuid === active.id);
      const newIndex = prev.findIndex((p) => p.uuid === over.id);
      return arrayMove(prev, oldIndex, newIndex).map((p, i) => ({
        ...p,
        position: i + 1,
      }));
    });
  }

  const handleDelete = (pUUID: string, photoUUID: string): void => {
    dispatch(photoDelete(pUUID, photoUUID));
  };

  const handleVisibleChange = (
    pUUID: string,
    photoUUID: string,
    isVisible: boolean,
  ): void => {
    dispatch(photoVisibleChange(pUUID, photoUUID, isVisible));
  };

  const saveAction = (): void => {
    dispatch(photoPositionSave(productUUID, localPhotos));
  };

  return (
    <>
      <Title level={4} style={{ margin: 0 }}>
        Photo ({productPhotos.length})
      </Title>

      <Button
        type="primary"
        htmlType="submit"
        disabled={productInProcess}
        onClick={(): void => saveAction()}
      >
        Submit
      </Button>

      <Divider />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(e: DragEndEvent): void => handleDragEnd(e)}
      >
        <SortableContext
          items={localPhotos.map((p) => p.uuid)}
          strategy={rectSortingStrategy}
        >
          <Row gutter={[SPACING.lg, SPACING.lg]}>
            {(() => {
              const priority: Record<DHashSimilarity, number> = {
                [DHashSimilarity.IDENTICAL]: 0,
                [DHashSimilarity.DUPLICATE]: 1,
                [DHashSimilarity.SIMILAR]: 2,
                [DHashSimilarity.DIFFERENT]: 3,
              };
              const worse = (
                a: DHashSimilarity,
                b: DHashSimilarity,
              ): DHashSimilarity => (priority[a] <= priority[b] ? a : b);

              const similarityMap = new Map<string, DHashSimilarity>();
              const withHash = localPhotos.filter(
                (p) => p.dhash && p.isVisible,
              );

              const pairs: { a: string; b: string }[] = [];
              for (let i = 0; i < withHash.length; i++) {
                for (let j = i + 1; j < withHash.length; j++) {
                  const a = withHash[i]!;
                  const b = withHash[j]!;
                  const s = classifyDHash(hammingDistance(a.dhash!, b.dhash!));
                  if (s === DHashSimilarity.DIFFERENT) continue;
                  pairs.push({ a: a.uuid, b: b.uuid });
                  similarityMap.set(
                    a.uuid,
                    worse(
                      s,
                      similarityMap.get(a.uuid) ?? DHashSimilarity.DIFFERENT,
                    ),
                  );
                  similarityMap.set(
                    b.uuid,
                    worse(
                      s,
                      similarityMap.get(b.uuid) ?? DHashSimilarity.DIFFERENT,
                    ),
                  );
                }
              }

              const parent = new Map<string, string>();
              const find = (x: string): string => {
                if (!parent.has(x)) parent.set(x, x);
                const p = parent.get(x)!;
                if (p !== x) parent.set(x, find(p));
                return parent.get(x)!;
              };
              pairs.forEach(({ a, b }) => parent.set(find(a), find(b)));

              const rootIndex = new Map<string, number>();
              const groupColorMap = new Map<string, string>();
              pairs.forEach(({ a, b }) => {
                [a, b].forEach((uuid) => {
                  const root = find(uuid);
                  if (!rootIndex.has(root)) {
                    rootIndex.set(root, rootIndex.size);
                  }
                  const color =
                    GROUP_COLORS[rootIndex.get(root)! % GROUP_COLORS.length];
                  groupColorMap.set(uuid, color!);
                });
              });

              return localPhotos.map((photo: IPhoto): React.ReactElement => (
                <SortablePhotoCard
                  key={photo.uuid}
                  photo={photo}
                  productUUID={productUUID}
                  similarity={similarityMap.get(photo.uuid)}
                  groupColor={groupColorMap.get(photo.uuid)}
                  onDelete={handleDelete}
                  onVisibleChange={handleVisibleChange}
                />
              ));
            })()}
          </Row>
        </SortableContext>
      </DndContext>

      <Divider />

      <Button
        type="primary"
        htmlType="submit"
        disabled={productInProcess}
        onClick={(): void => saveAction()}
      >
        Submit
      </Button>

      <Divider />

      <Dragger
        name="photo"
        multiple
        action={`/api/photo/${productUUID}`}
        onChange={(info): void => {
          const { status } = info.file;

          if (status === 'done') {
            notify(
              ENotificationSeverity.SUCCESS,
              `${info.file.name} file uploaded successfully.`,
            );
          } else if (status === 'error') {
            notify(
              ENotificationSeverity.ERROR,
              `${info.file.name} file upload failed.`,
            );
          }
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Click or drag file to this area to upload
        </p>
        <p className="ant-upload-hint">
          Support for a single or bulk upload. Strictly prohibit from uploading
          company data or other band files
        </p>
      </Dragger>

      <Title level={3}>Download image from this urls</Title>
      <Form
        form={form}
        onFinish={({ urls }: IForm): void => {
          const urlsFiltered: Set<string> = new Set();
          urls.forEach((url: IFormUrl): Set<string> =>
            urlsFiltered.add(url.url),
          );
          dispatch(photoDownloadFromUrlsAction(productUUID, [...urlsFiltered]));
        }}
        layout="vertical"
      >
        <Form.Item
          name="urls"
          label="URL"
          rules={[
            {
              required: true,
              message: 'Please input a URL',
            },
          ]}
        >
          <Form.List
            name="urls"
            rules={[
              {
                validator: async (_, urls): Promise<void> => {
                  if (!urls || urls.length < 1) {
                    return Promise.reject(new Error('At least 1 URL'));
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            {(fields, { add, remove }: FormListOperation, { errors }) => (
              <>
                {fields.map(
                  ({
                    key,
                    ...field
                  }: FormListFieldData): React.ReactElement => (
                    <Space
                      key={key}
                      style={{
                        display: 'flex',
                        marginBottom: SPACING.sm,
                        width: '100%',
                      }}
                      align="baseline"
                    >
                      <Form.Item
                        {...field}
                        name={[field.name, 'url']}
                        rules={[
                          {
                            required: true,
                            message: 'Missing URL',
                          },
                        ]}
                      >
                        <Input placeholder="URL" />
                      </Form.Item>

                      <MinusCircleOutlined
                        onClick={(): void => remove(field.name)}
                      />
                    </Space>
                  ),
                )}

                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={(): void => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add URL
                  </Button>

                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" disabled={productInProcess}>
            Download
          </Button>
        </Form.Item>
      </Form>
    </>
  );
}

export default React.memo(Photo);
